import React, { useState } from 'react';
import { Form, Input, Button, Card, Typography, Alert, Result } from 'antd';
import { IdcardOutlined, LockOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import api from '../api/axios';
import { apiErrorMessage } from '../utils/apiError';
import useSettingsStore from '../store/settingsStore';
import PhoneInput from '../components/common/PhoneInput';

const { Title, Paragraph, Text } = Typography;

/**
 * Veli üyeliğini aktifleştirme — GİRİŞ GEREKTİRMEZ.
 *
 * Portal ~500 veliye açılıyor. Herkese aynı mesaj gidiyor ("siteye girin,
 * telefonunuz ve çocuğunuzun T.C. numarasıyla şifrenizi kurun"), ama
 * paylaşılan bir şifre hiç var olmuyor.
 *
 * Ekran, veliye teknik terim göstermeden ne yapması gerektiğini söylemeli:
 * bu sayfayı açacak kişilerin çoğu sistemi ilk kez kullanıyor.
 */
const ParentActivate = () => {
  const navigate = useNavigate();
  const { appName } = useSettingsStore();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [hata, setHata]       = useState('');
  const [bitti, setBitti]     = useState(false);

  const gonder = async (values) => {
    setLoading(true);
    setHata('');
    try {
      await api.post('/auth/parent/activate', {
        phone:                 values.phone,
        tc_no:                 values.tc_no,
        password:              values.password,
        password_confirmation: values.password_confirmation,
      });
      setBitti(true);
    } catch (e) {
      setHata(apiErrorMessage(e, 'Üyelik aktifleştirilemedi. Lütfen tekrar deneyin.'));
    } finally {
      setLoading(false);
    }
  };

  if (bitti) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <Card style={{ maxWidth: 520, width: '100%' }}>
          <Result
            status="success"
            title="Üyeliğiniz aktifleştirildi"
            subTitle="Artık telefon numaranız ve belirlediğiniz şifreyle giriş yapabilirsiniz."
            extra={
              <Button type="primary" size="large" onClick={() => navigate('/login')}>
                Giriş Yap
              </Button>
            }
          />
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
      <Card style={{ maxWidth: 520, width: '100%' }}>
        <div className="text-center mb-4">
          <Title level={3} style={{ marginBottom: 4 }}>Üyeliğimi Aktifleştir</Title>
          <Text type="secondary">{appName || 'ARYS'} veli girişi</Text>
        </div>

        <Paragraph type="secondary" style={{ fontSize: 14 }}>
          İlk kez giriş yapıyorsanız buradan şifrenizi belirleyin. Kurumda kayıtlı
          telefon numaranız ile çocuğunuzun T.C. kimlik numarasını girmeniz yeterli.
        </Paragraph>

        {hata && (
          <Alert type="error" showIcon message={hata} style={{ marginBottom: 16 }} />
        )}

        <Form form={form} layout="vertical" onFinish={gonder} requiredMark={false}>
          <Form.Item
            name="phone"
            label="Telefon Numaranız"
            rules={[{ required: true, message: 'Telefon numaranızı girin.' }]}
          >
            {/* Biçim DAYATILMIYOR: sunucu "0532...", "+90 532...", "532..."
                hepsini aynı numaraya çeviriyor. Veliye biçim kuralı koymak
                boşuna başarısız deneme ve "giremiyorum" telefonu üretir.

                ÜLKE SEÇİCİ ŞART: aktifleştirme telefon + öğrenci TC ile
                eşleştiriliyor. Yurt dışı numaralı veli ülke kodunu
                giremediği sürece hesabını HİÇ açamıyordu. */}
            <PhoneInput
              size="large"
              autoComplete="tel"
            />
          </Form.Item>

          <Form.Item
            name="tc_no"
            label="Çocuğunuzun T.C. Kimlik Numarası"
            extra="Birden fazla çocuğunuz varsa herhangi birininki yeterli."
            rules={[
              { required: true, message: 'T.C. kimlik numarasını girin.' },
              { pattern: /^\d{11}$/, message: 'T.C. kimlik numarası 11 haneli olmalı.' },
            ]}
          >
            <Input
              size="large"
              prefix={<IdcardOutlined />}
              placeholder="11 haneli"
              maxLength={11}
              inputMode="numeric"
            />
          </Form.Item>

          <Form.Item
            name="password"
            label="Belirleyeceğiniz Şifre"
            rules={[
              { required: true, message: 'Şifre belirleyin.' },
              { min: 8, message: 'Şifre en az 8 karakter olmalı.' },
            ]}
            hasFeedback
          >
            <Input.Password size="large" prefix={<LockOutlined />} placeholder="En az 8 karakter" />
          </Form.Item>

          <Form.Item
            name="password_confirmation"
            label="Şifre (Tekrar)"
            dependencies={['password']}
            hasFeedback
            rules={[
              { required: true, message: 'Şifreyi tekrar girin.' },
              ({ getFieldValue }) => ({
                validator: (_, value) =>
                  !value || getFieldValue('password') === value
                    ? Promise.resolve()
                    : Promise.reject(new Error('Şifreler aynı değil.')),
              }),
            ]}
          >
            <Input.Password size="large" prefix={<LockOutlined />} placeholder="Şifrenizi tekrar yazın" />
          </Form.Item>

          <Button type="primary" htmlType="submit" size="large" block loading={loading}>
            Üyeliğimi Aktifleştir
          </Button>
        </Form>

        <div className="text-center mt-4">
          <Button type="link" onClick={() => navigate('/login')}>
            Şifremi belirledim, giriş yapmak istiyorum
          </Button>
        </div>

        <Paragraph type="secondary" style={{ fontSize: 12, marginTop: 8, marginBottom: 0 }}>
          Bilgileriniz eşleşmiyorsa ya da daha önce şifre belirlediyseniz kurumla
          iletişime geçin.
        </Paragraph>
      </Card>
    </div>
  );
};

export default ParentActivate;
